/**
 * Design Tokens - Colors
 *
 * Brand: FINN.AI
 * Aesthetic: Bloomberg Terminal × Duolingo
 * Palette: Near-black base · Electric teal accent · Amber highlight
 *
 * Background spec (always dark - no light mode):
 *   linear-gradient(180deg, #010204 0%, #000101 100%),
 *   radial-gradient(36.46% 41.67% at 100% 100%, rgba(0,19,64,0.25) 0%, rgba(0,19,64,0) 60%)
 */

export const colors = {
  // ── Brand Primary (Electric Teal) ──────────────────────────────
  primary: {
    50: '#e6fbfb',
    100: '#b3f3f3',
    200: '#80ebeb',
    300: '#4de3e3',
    400: '#26d9d9',
    500: '#00cfcf', // Core teal accent
    600: '#00b8b8',
    700: '#009e9e',
    800: '#008585',
    900: '#006060',
  },

  // ── Near-Black Base (Background / Surface) ─────────────────────
  // Derived from the design-spec gradient endpoints + step-up surfaces
  dark: {
    50: '#1a1f2e',   // lightest raised surface (modals, dropdowns)
    100: '#12171f',   // card / panel
    200: '#0d1117',   // elevated surface
    300: '#090c11',   // primary surface
    400: '#05080d',   // deep surface
    500: '#020408',   // near root
    base: '#010204',  // gradient top   - use for backgrounds
    deep: '#000101',  // gradient bottom - use for backgrounds
    accent: 'rgba(0, 19, 64, 0.25)', // radial accent tint
  },

  // ── Amber (Highlight / Warning) ────────────────────────────────
  amber: {
    50: '#fff8e6',
    100: '#ffedb3',
    200: '#ffe180',
    300: '#ffd54d',
    400: '#ffca26',
    500: '#ffc107', // Core amber
    600: '#e6a800',
    700: '#cc9400',
    800: '#b38100',
    900: '#806000',
  },

  // ── Neutral (Slate Greys) ─────────────────────────────────────
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // ── Semantic ──────────────────────────────────────────────────
  success: '#10b981',
  warning: '#ffc107',
  error: '#ef4444',
  info: '#3b82f6',

  // ── UI Role Aliases ───────────────────────────────────────────
  // These map to the gradient-based background system
  background: '#010204',   // gradient top stop
  backgroundDeep: '#000101',   // gradient bottom stop
  surface: '#0d1117',   // card / panel
  surfaceElevated: '#12171f',   // modal / dropdown
  border: 'rgba(0, 207, 207, 0.15)',
  borderHover: 'rgba(0, 207, 207, 0.38)',
  borderSubtle: 'rgba(255, 255, 255, 0.06)',

  text: {
    primary: '#f0f8ff', // Alice Blue - crisp on near-black
    secondary: '#94a3b8', // neutral-400
    muted: '#64748b', // neutral-500
    disabled: '#475569', // neutral-600
    inverse: '#010204', // for text on teal/amber CTAs
  },

  // ── Glow helpers ─────────────────────────────────────────────
  glow: {
    teal: 'rgba(0, 207, 207, 0.28)',
    tealLg: 'rgba(0, 207, 207, 0.10)',
    amber: 'rgba(255, 193, 7, 0.28)',
    amberLg: 'rgba(255, 193, 7, 0.10)',
    blue: 'rgba(0, 19, 64, 0.25)',  // radial accent from spec
  },

  // ── Component & Feature Specific Tokens ───────────────────────
  button: {
    primary: {
      bg: 'rgba(0, 207, 207, 0.08)',
      border: 'rgba(0, 207, 207, 0.25)',
      text: '#f0f8ff',
      hoverBg: 'rgba(0, 207, 207, 0.14)',
      hoverBorder: 'rgba(0, 207, 207, 0.45)',
      glow: 'rgba(0, 207, 207, 0.18)',
    },
    secondary: {
      bg: '#B0B0B0',
      border: 'transparent',
      text: '#000000',
      hoverBg: '#c2c2c2',
    },
    ghost: {
      text: 'rgba(255, 255, 255, 0.45)',
      hoverText: 'rgba(255, 255, 255, 0.72)',
    },
    danger: {
      bg: 'rgba(239, 68, 68, 0.08)',
      border: 'rgba(239, 68, 68, 0.25)',
      text: '#fca5a5',
      hoverBg: 'rgba(239, 68, 68, 0.14)',
      hoverBorder: 'rgba(239, 68, 68, 0.4)',
    },
    glass: {
      bg: 'rgba(255, 255, 255, 0.03)',
      border: 'rgba(255, 255, 255, 0.10)',
      text: 'rgba(255, 255, 255, 0.90)',
      hoverBg: 'rgba(255, 255, 255, 0.06)',
      hoverBorder: 'rgba(255, 255, 255, 0.18)',
    },
    export: {
      bg: '#A3A3A3',
      text: '#000000',
    },
  },

  auth: {
    heading: '#B0B0B0',
    subheading: '#869CA7',
    link: '#2151A1',
    linkHover: 'rgba(33, 81, 161, 0.85)',
    stepActiveBg: '#2151A1',
    stepActiveBorder: 'rgba(33, 81, 161, 0.7)',
    stepActiveGlow: 'rgba(33, 81, 161, 0.4)',
    stepActiveText: '#ffffff',
    stepInactiveBg: '#7C8891',
    stepInactiveBorder: 'rgba(255, 255, 255, 0.12)',
    stepInactiveText: '#020A15',
    inputBg: '#020A15',
    inputBorder: 'rgba(255, 255, 255, 0.078)',
    inputLabel: '#869CA7',
  },

  status: {
    warning: '#F59E0B',
    warningText: '#000000',
    sky: '#38bdf8',
  },

  chart: {
    green: '#74DD66',
    greenDark: '#011F01',
  },

  brand: {
    zoom: '#2D8CFF',
    meet: '#0066da',
    meetSecondary: '#00832d',
  },

  glass: {
    surface: 'rgba(255, 255, 255, 0.05)',
    subtle: 'rgba(255, 255, 255, 0.02)',
    skeleton: 'rgba(255, 255, 255, 0.08)',
  },

  dropdown: {
    bg: '#09090B',
    glassBg: 'rgba(10, 15, 20, 0.7)',
  },
} as const;
